import { apiFetch, apiUpload, buildQuery } from "./client";
import type { Page } from "./admin";
import type { AttributeDefView, AttributeValue } from "./catalog";
import type { LocationKindIcon } from "./locations";
import type {
  AssetEventView,
  AssetSummary,
  DismountAllRequest,
  DismountAllResult,
  MountCandidate,
  MountState,
  PageParams,
} from "./assets";

/**
 * Sprint 2 contract: docs/api/sprint2-struktura.md §2-§3; sprint 3 additions: sprint3-asseti.md §4;
 * sprint 4 additions (templates): sprint4-sabloni-prilozi-komentari.md §2; sprint 5c drops the
 * type's default template (sprint5c-tip-dela-samo-atributi.md §3).
 */

/**
 * Code of a `position_kind` row — an admin-defined codebook since V12
 * (sprint 5e), no longer a fixed union. Any kind may sit on the first level
 * (there is no dedicated root, V11).
 */
export type PositionKind = string;

/** Icon palette shared with location kinds (same CHECK list server-side). */
export type PositionKindIconName = LocationKindIcon;

/** Code regex enforced server-side (`^[A-Z][A-Z0-9_]{1,29}$`). */
export const POSITION_KIND_CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,29}$/;

export type PositionKindView = {
  code: string;
  name: string;
  icon: PositionKindIconName;
  /** `#RRGGBB` of the dot shown next to positions of this kind; null = no dot. */
  color: string | null;
  /**
   * Whether a position of this kind is a slot for one concrete asset (empty =
   * red) or a structural node (no own mount state; the layout aggregates its
   * children). 2026-09-11.
   */
  holdsAsset: boolean;
  sortOrder: number;
  positionCount: number;
};

export type PositionKindRequest = {
  /** Ignored on PUT. */
  code: string;
  name: string;
  sortOrder?: number;
  /** Defaults to box on POST; omitted on PUT keeps the current icon. */
  icon?: PositionKindIconName;
  /** Always taken as sent: `#RRGGBB` or null (no dot). */
  color: string | null;
  /** POST defaults to true; PUT takes it as sent. */
  holdsAsset?: boolean;
};

export const positionKindsApi = {
  list: () => apiFetch<PositionKindView[]>("/position-kinds"),
  create: (body: PositionKindRequest) =>
    apiFetch<PositionKindView>("/position-kinds", { method: "POST", body }),
  update: (code: string, body: PositionKindRequest) =>
    apiFetch<PositionKindView>(`/position-kinds/${encodeURIComponent(code)}`, { method: "PUT", body }),
  remove: (code: string) =>
    apiFetch<void>(`/position-kinds/${encodeURIComponent(code)}`, { method: "DELETE" }),
};

/**
 * Fallback labels for the six kinds seeded by V12 — used where the codebook is
 * not loaded (history diffs); live screens resolve names through `usePositionKinds`.
 */
export const POSITION_KIND_LABEL: Record<string, string> = {
  SITE: "Lokacija",
  PLANT: "Pogon",
  LINE: "Linija",
  MACHINE: "Mašina",
  ASSEMBLY: "Sklop",
  PART: "Deo",
};

export type PositionNode = {
  id: string;
  parentId: string | null;
  code: string | null;
  name: string;
  kind: PositionKind;
  sequenceNo: number | null;
  sortOrder: number;
  expectedTypeId: string | null;
  expectedTypeCode: string | null;
  expectedTypeName: string | null;
  /** Active children only. */
  hasChildren: boolean;
  childCount: number;
  mountedAssetId: string | null;
  mountedAssetTag: string | null;
  mountState: MountState;
  /**
   * Positions below this one, at any depth, whose kind holds an asset — and how many of them are
   * empty: the "2/13" counter on a structural node (2026-10-15). Tree only, active rows only.
   */
  slotsBelow: number;
  emptySlotsBelow: number;
  /** null = not loaded yet (lazy); [] = no children. */
  children: PositionNode[] | null;
  /** Set on soft-deleted nodes — only present when the tree was asked with `includeDeleted` (2026-09-22). */
  deletedAt?: string | null;
  /** Mandatory reason given on delete (2026-09-22); null while active. */
  deletedReason?: string | null;
  /** Who deleted it (display name from the audit log); null while active or unknown. */
  deletedByName?: string | null;
};

export type PositionPathEntry = { id: string; name: string; code: string | null };

export type PositionView = Omit<PositionNode, "children" | "slotsBelow" | "emptySlotsBelow"> & {
  /** Root -> parent (without the node itself). */
  path: PositionPathEntry[];
};

export type EffectiveLocation = {
  id: string;
  code: string;
  name: string;
  path: { id: string; name: string }[];
  /** Effective GPS of that location (own or nearest ancestor's); null when unknown. */
  lat: number | null;
  lng: number | null;
};

export type PositionDetail = PositionView & {
  referenceValues: Record<string, AttributeValue>;
  locationId: string | null;
  effectiveLocation: EffectiveLocation | null;
  drawingCode: string | null;
  interventionNote: string | null;
  note: string | null;
  /** Attribute definitions of the expected type; [] when no type is expected. */
  attributeDefs: AttributeDefView[];
  descendantCount: number;
  mountedAsset: AssetSummary | null;
  mountState: MountState;
  /** Sprint 6: active layouts of this position (`GET /positions/{id}/layouts`). */
  layoutCount: number;
  /** Sprint 6: length of `GET /positions/{id}/placements` — where it (or an ancestor) is marked. */
  placementCount: number;
  /** Sprint 5l: lines of `GET /positions/{id}/bom` — the card skips the fetch when 0 (hard rule 11). */
  bomCount: number;
  createdAt: string;
  updatedAt: string;
};

export type PositionRequest = {
  /** null = first level; ignored on PUT (moving goes through `move`). */
  parentId: string | null;
  code?: string | null;
  name: string;
  kind: PositionKind;
  expectedTypeId?: string | null;
  /** Replaced whole on PUT; must be empty when no type is expected. */
  referenceValues?: Record<string, unknown>;
  locationId?: string | null;
  drawingCode?: string | null;
  interventionNote?: string | null;
  note?: string | null;
  sortOrder?: number;
  /** POST only; 1..50; default 1. */
  quantity?: number;
};

/** Sprint 4 §2: `quantity` 1..20; >1 numbers the created roots (`sequenceNo`). */
export type ApplyTemplateRequest = { templateId: string; quantity?: number };

/** One position of `GET /positions/{id}/branch-values` (2026-10-20); the position itself is included. */
export type BranchValueEntry = {
  id: string;
  parentId: string | null;
  expectedTypeId: string | null;
  referenceValues: Record<string, AttributeValue>;
};

/** Reference values of a whole branch for "Sačuvaj kao šablon" (sprint 4 §2a). */
export type BranchValues = {
  positions: BranchValueEntry[];
  /** Definitions per expected type id; a type without any is absent. */
  attributeDefs: Record<string, AttributeDefView[]>;
};

/** `parentId` null = to the first level. */
export type MoveRequest = { parentId: string | null; sortOrder?: number };

/** `quantity` 1..20; `targetParentId` null = first level. */
/** `excludeIds`: positions below the copied one that are left out, each with everything below it. */
export type CopyRequest = { targetParentId: string | null; name?: string | null; code?: string | null; quantity?: number; excludeIds?: string[] };

export type StructureTreeParams = {
  parentId?: string;
  /** Levels filled below the returned nodes: 1 = returned nodes carry `children: null`; default 2; max 4. */
  depth?: number;
  /** "Prikaži obrisane": soft-deleted positions come back too, marked with `deletedAt`. */
  includeDeleted?: boolean;
};

export type PositionListParams = {
  q?: string;
  kind?: PositionKind;
  expectedTypeId?: string;
  /** Direct children only. */
  parentId?: string;
  /** Same rule as the tree badge: EMPTY = slot with nothing mounted, NONE = structural kind. */
  mountState?: MountState;
  page?: number;
  size?: number;
  sort?: string;
};

export const structureApi = {
  /** Children of `parentId` (without it: the first level). */
  tree: (params: StructureTreeParams = {}) =>
    apiFetch<PositionNode[]>(`/structure/tree${buildQuery(params)}`),
  list: (params: PositionListParams = {}) =>
    apiFetch<Page<PositionView>>(`/positions${buildQuery(params)}`),
  get: (id: string) => apiFetch<PositionDetail>(`/positions/${id}`),
  /** 201; one detail per created row (`quantity`). */
  create: (body: PositionRequest) =>
    apiFetch<PositionDetail[]>("/positions", { method: "POST", body }),
  update: (id: string, body: PositionRequest) =>
    apiFetch<PositionDetail>(`/positions/${id}`, { method: "PUT", body }),
  /** 409 CYCLE. */
  move: (id: string, body: MoveRequest) =>
    apiFetch<PositionDetail>(`/positions/${id}/move`, { method: "POST", body }),
  /** 201; copies the sub-tree (minus `excludeIds`), one detail per copy root. 400 VALIDATION (details.excludeIds). */
  copy: (id: string, body: CopyRequest) =>
    apiFetch<PositionDetail[]>(`/positions/${id}/copy`, { method: "POST", body }),
  /** 409 HAS_CHILDREN (details.childCount) unless `cascade`. */
  /** 409 HAS_MOUNTED_ASSETS (details.count) before HAS_CHILDREN (details.childCount) unless `cascade`. */
  /** `reason` is mandatory (400 VALIDATION, details.reason) — 2026-09-22. */
  remove: (id: string, cascade = false, reason = "") =>
    apiFetch<void>(`/positions/${id}${buildQuery({ cascade: cascade ? true : undefined, reason })}`, { method: "DELETE" }),
  /** Undo of a soft delete (with the descendants deleted in the same cascade). 400 NOT_DELETED, 409 PARENT_DELETED / CODE_TAKEN. */
  /** `code`: undefined = keep, "" = without a code, otherwise the new code (after 409 CODE_TAKEN, details.heldBy). */
  /** 409 CHILD_CODE_TAKEN (details.codes) when restored sub-positions' codes are taken; `dropChildCodes` restores them without a code. */
  restore: (id: string, code?: string, dropChildCodes = false) => {
    // Built by hand: an empty `code` MUST reach the server (it means "restore without a code"),
    // while the shared query builder drops empty values.
    const q: string[] = [];
    if (code !== undefined) q.push(`code=${encodeURIComponent(code)}`);
    if (dropChildCodes) q.push("dropChildCodes=true");
    return apiFetch<PositionDetail>(`/positions/${id}/restore${q.length ? `?${q.join("&")}` : ""}`, { method: "POST" });
  },

  // ---- sprint 3 ----------------------------------------------------------
  /** Max 50 ISPRAVAN, unmounted assets sorted by matchLevel then tag; ANY only with `q` or when nothing is expected. */
  mountCandidates: (id: string, q?: string) =>
    apiFetch<MountCandidate[]>(`/positions/${id}/mount-candidates${buildQuery({ q })}`),
  /** Position history: MOUNT / DISMOUNT events with that position, occurredAt DESC. */
  events: (id: string, params: PageParams = {}) =>
    apiFetch<Page<AssetEventView>>(`/positions/${id}/events${buildQuery(params)}`),
  /** Every mounted asset in the sub-tree (incl. the position) → toLocationId; 409 NOTHING_MOUNTED. */
  dismountAll: (id: string, body: DismountAllRequest) =>
    apiFetch<DismountAllResult>(`/positions/${id}/dismount-all`, { method: "POST", body }),

  // ---- sprint 4 ----------------------------------------------------------
  /** 201; template nodes become new positions under `id`; one detail per created root. */
  applyTemplate: (id: string, body: ApplyTemplateRequest) =>
    apiFetch<PositionDetail[]>(`/positions/${id}/apply-template`, { method: "POST", body }),
  /** Every active position of the branch with its reference values + the definitions of their types (2026-10-20). */
  branchValues: (id: string) => apiFetch<BranchValues>(`/positions/${id}/branch-values`),
};

// ---------------------------------------------------------------------------
// Sastavnica of a position (sprint 5l: docs/api/sprint5l-sastavnica-i-artikli.md §4)
// ---------------------------------------------------------------------------

/**
 * One line of a position's sastavnica: the article as the row shows it (code, name, type, unit —
 * readable with `bom:view` alone, decision 8) and the quantity in the article's unit.
 */
export type BomItemView = {
  id: string;
  positionId: string;
  articleId: string;
  articleCode: string | null;
  articleName: string | null;
  articleTypeId: string | null;
  articleTypeName: string | null;
  /** Unit symbol of the article; the quantity is counted in it. */
  unit: string | null;
  /** The article was deleted since (possible only on a deleted position). */
  articleDeleted: boolean;
  /** > 0, up to three decimals. */
  quantity: number;
  note: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export type BomItemRequest = {
  articleId: string;
  /** > 0, up to 7 integer digits and 3 decimals. */
  quantity: number;
  note?: string | null;
  /** Default: the end of the list. */
  sortOrder?: number | null;
};

/** The article of a line never changes — a different article is a new line. */
export type BomItemUpdateRequest = { quantity: number; note?: string | null; sortOrder?: number | null };

export const bomApi = {
  /** By sortOrder then article code; works on a deleted position too (the card is readable). */
  list: (positionId: string) => apiFetch<BomItemView[]>(`/positions/${positionId}/bom`),
  /**
   * 201; 404 on a deleted position; 400 VALIDATION (`details.articleId` = "not found", `details.quantity`);
   * 409 ARTICLE_TAKEN with `details.itemId` = the line that already carries the article.
   */
  add: (positionId: string, body: BomItemRequest) =>
    apiFetch<BomItemView>(`/positions/${positionId}/bom`, { method: "POST", body }),
  update: (positionId: string, itemId: string, body: BomItemUpdateRequest) =>
    apiFetch<BomItemView>(`/positions/${positionId}/bom/${itemId}`, { method: "PUT", body }),
  /** 204, no reason (a child row like a layout marker); the audit log keeps the row. */
  remove: (positionId: string, itemId: string) =>
    apiFetch<void>(`/positions/${positionId}/bom/${itemId}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Import of a machine's structure from a CAD export (sprint 5j, a trial)
// ---------------------------------------------------------------------------

/** Why a line is left unticked when the preview opens. */
/** Why a line starts unticked (rules by the ID, 2026-10-20): an ID that is not six digits, or no ID at all. */
export type BomCategory = "OTHER_ID" | "NO_ID";

export type BomNode = {
  /** CAD model name (column "IME MODELA NA H.D.") — the proposed position name. */
  model: string;
  /** Name from the drawing, when the file has one. */
  title: string | null;
  /** Column "ID.BROJ": drawing number or standard. */
  drawingCode: string | null;
  material: string | null;
  /** Pieces of this model in its parent. */
  quantity: number;
  /** Proposed position kind code; null when the codebook has no such kind. */
  kind: string | null;
  category: BomCategory | null;
  children: BomNode[];
};

export type BomPreview = { fileName: string; items: number; roots: BomNode[] };

export type StructureImportNode = {
  name: string;
  kind: string;
  drawingCode?: string | null;
  note?: string | null;
  /** N pieces = N positions #1…#N, each with its own copy of `children`. */
  quantity?: number;
  /** Part type the positions expect (2026-10-20); only a kind that holds an asset takes one. */
  expectedTypeId?: string | null;
  children?: StructureImportNode[];
};

export type StructureImportRequest = { targetParentId: string | null; roots: StructureImportNode[] };

export type StructureImportResult = { rootIds: string[]; created: number };

/** Drafts of the import (sprint 5j §3a, 2026-10-20): a file becomes a draft at once; the review is saved as it goes. */
export type ImportDraftStatus = "DRAFT" | "IMPORTED";

/** What the user changed on one line, keyed by the line's path id ("0.3.1"); only what differs from the proposal. */
export type ImportDraftEdit = { included?: boolean; name?: string; kind?: string; expectedTypeId?: string };

export type ImportDraftView = {
  id: string;
  name: string;
  fileName: string;
  status: ImportDraftStatus;
  itemCount: number;
  selectedCount: number;
  positionCount: number;
  incompleteCount: number;
  targetParentId: string | null;
  /** Name of the active target position; null without one. */
  targetName: string | null;
  /** A target was picked, but the position is gone (deleted) since. */
  targetMissing: boolean;
  toTopLevel: boolean;
  createdByName: string | null;
  createdAt: string;
  updatedByName: string | null;
  updatedAt: string;
  importedAt: string | null;
  importedByName: string | null;
  /** The machine the import created (first position of the first root). */
  importRootId: string | null;
  importedCount: number | null;
  /** Optimistic lock: send back the version that was loaded (409 DRAFT_STALE otherwise). */
  version: number;
};

export type ImportDraftDetail = ImportDraftView & { preview: BomPreview; edits: Record<string, ImportDraftEdit> };

export type ImportDraftUpdate = {
  name: string;
  edits: Record<string, ImportDraftEdit>;
  targetParentId: string | null;
  toTopLevel: boolean;
  version: number;
};

export const importDraftsApi = {
  /** Newest change first; without `status` every draft that is not deleted. */
  list: (status?: ImportDraftStatus) =>
    apiFetch<ImportDraftView[]>(`/structure/import/drafts${buildQuery({ status })}`),
  /** 201; the file is read like the preview and kept as a draft (same 400 codes). */
  create: (file: File, targetParentId?: string | null) =>
    apiUpload<ImportDraftDetail>(`/structure/import/drafts${buildQuery({ targetParentId: targetParentId ?? undefined })}`, file),
  get: (id: string) => apiFetch<ImportDraftDetail>(`/structure/import/drafts/${id}`),
  /** 409 DRAFT_STALE (details.version) / DRAFT_IMPORTED; 400 VALIDATION (details.name / edits / targetParentId). */
  update: (id: string, body: ImportDraftUpdate) =>
    apiFetch<ImportDraftView>(`/structure/import/drafts/${id}`, { method: "PUT", body }),
  /** `reason` is mandatory; 409 DRAFT_IMPORTED (an imported draft stays as history). */
  remove: (id: string, reason: string) =>
    apiFetch<void>(`/structure/import/drafts/${id}${buildQuery({ reason })}`, { method: "DELETE" }),
  /** 201; writes the tree and marks the draft imported in one go; 409 DRAFT_IMPORTED on a second try. */
  run: (id: string, body: StructureImportRequest) =>
    apiFetch<StructureImportResult>(`/structure/import/drafts/${id}/import`, { method: "POST", body }),
};

